import { useInterval } from 'ahooks'
import dayjs from 'dayjs'
import { useCallback, useEffect, useRef, useState } from 'react'

import { API } from '@/api'
import { enumToHostChartType, extractTimePoints, HostChartType } from '@/models/chart'
import { TIME_UNITS_IN_SECONDS } from '@/utils/time'

interface UseNodeMonitorDataParams {
  nodeId: string
  filterDuration: number
}

export interface HostMonitorItemData {
  type: HostChartType
  titleKey: string
  realtimeValue: string
  values: HostMonitorValue[]
  isEmpty: boolean
}

export interface HostMonitorValue {
  time: string
  value: number | undefined
  unit: string
  rawValue?: number // Original bytes value for dynamic formatting
  // Aggregated stats (only present when duration > HOUR)
  avg?: number
  max?: number
  min?: number
  last?: number
  rawAvg?: number
  rawMax?: number
  rawMin?: number
  rawLast?: number
}

export interface NetworkInterfaceData {
  name: string
  txValues: HostMonitorValue[]
  rxValues: HostMonitorValue[]
  isEmpty: boolean
}

export interface DiskInterfaceData {
  name: string
  readValues: HostMonitorValue[]
  writeValues: HostMonitorValue[]
  isEmpty: boolean
}

export interface GpuDeviceData {
  name: string
  values: HostMonitorValue[]
  isEmpty: boolean
}

// Chart title i18n key mapping
const chartTitleKeyMap: Record<HostChartType, string> = {
  [HostChartType.UptimeRate]: 'nodeMonitor.chartTitle.uptimeRate',
  [HostChartType.CpuUtilization]: 'nodeMonitor.chartTitle.cpuUtilization',
  [HostChartType.MemoryUtilization]: 'nodeMonitor.chartTitle.memoryUtilization',
  [HostChartType.DiskUtilization]: 'nodeMonitor.chartTitle.diskUtilization',
  [HostChartType.DiskIOReadBytesPerSec]: 'nodeMonitor.chartTitle.diskRead',
  [HostChartType.DiskIOWriteBytesPerSec]: 'nodeMonitor.chartTitle.diskWrite',
  [HostChartType.NetworkReceiveBytesPerSec]: 'nodeMonitor.chartTitle.bandwidthUsage',
  [HostChartType.NetworkTransmitBytesPerSec]: 'nodeMonitor.chartTitle.bandwidthUsage',
  [HostChartType.NvidiaGpuCount]: 'nodeMonitor.chartTitle.gpuCount',
  [HostChartType.NvidiaDriverVersion]: 'nodeMonitor.chartTitle.driverVersion',
  [HostChartType.NvidiaGpuUtilization]: 'nodeMonitor.chartTitle.gpuUtilization',
  [HostChartType.NvidiaGpuRamUtilization]: 'nodeMonitor.chartTitle.gpuRamUtilization',
}

function formatRealtimeValue(chartType: HostChartType, item: HostMonitorValue): string {
  const isPercentage =
    chartType === HostChartType.CpuUtilization ||
    chartType === HostChartType.MemoryUtilization ||
    chartType === HostChartType.DiskUtilization ||
    chartType === HostChartType.NvidiaGpuUtilization ||
    chartType === HostChartType.NvidiaGpuRamUtilization

  return isPercentage ? `${item.value || 0}%` : `${item.value || 0}${item.unit}`
}

function formatHostValue(chartType: HostChartType, value: number): { value: number; unit: string } {
  // Percentage-based metrics
  if (
    chartType === HostChartType.CpuUtilization ||
    chartType === HostChartType.MemoryUtilization ||
    chartType === HostChartType.DiskUtilization ||
    chartType === HostChartType.NvidiaGpuUtilization ||
    chartType === HostChartType.NvidiaGpuRamUtilization ||
    chartType === HostChartType.UptimeRate
  ) {
    return { value, unit: '%' }
  }

  // Bytes-based metrics
  if (
    chartType === HostChartType.DiskIOReadBytesPerSec ||
    chartType === HostChartType.DiskIOWriteBytesPerSec ||
    chartType === HostChartType.NetworkReceiveBytesPerSec ||
    chartType === HostChartType.NetworkTransmitBytesPerSec
  ) {
    return bytesToSize(value)
  }

  // Count/version metrics
  return { value, unit: '' }
}

export function bytesToSize(bytes: number) {
  if (bytes === 0) return { value: 0, unit: 'B' }
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return {
    value: parseFloat((bytes / Math.pow(k, i)).toFixed(2)),
    unit: sizes[i],
  }
}

export function formatValueWithUnit(value: number, unit: string) {
  if (unit === '%') return parseFloat(value.toFixed(2))
  if (unit === 'B') return value
  if (unit === 'KB') return parseFloat((value / 1024).toFixed(2))
  if (unit === 'MB') return parseFloat((value / 1024 / 1024).toFixed(2))
  if (unit === 'GB') return parseFloat((value / 1024 / 1024 / 1024).toFixed(2))
  if (unit === 'TB') return parseFloat((value / 1024 / 1024 / 1024 / 1024).toFixed(2))
  return value
}

function convertToHostMonitorValue(
  v: { time: dayjs.Dayjs; value?: number },
  fmax: { value: number; unit: string },
): HostMonitorValue {
  return {
    time: v.time.format(),
    value: v.value !== undefined ? formatValueWithUnit(v.value, fmax.unit) : undefined,
    unit: fmax.unit,
    rawValue: v.value,
  }
}

// Aggregate raw data points into hourly buckets
function aggregateToHourly(
  data: { time: dayjs.Dayjs; value?: number }[],
  fmax: { value: number; unit: string },
): HostMonitorValue[] {
  if (data.length === 0) return []

  // Group by hour
  const hourlyBuckets = new Map<string, number[]>()

  for (const point of data) {
    if (point.value === undefined) continue
    const hourKey = point.time.startOf('hour').format()
    const bucket = hourlyBuckets.get(hourKey) || []
    bucket.push(point.value)
    hourlyBuckets.set(hourKey, bucket)
  }

  // Convert to aggregated values
  const result: HostMonitorValue[] = []

  for (const [hourKey, values] of hourlyBuckets) {
    if (values.length === 0) continue

    const sum = values.reduce((a, b) => a + b, 0)
    const avg = sum / values.length
    const max = Math.max(...values)
    const min = Math.min(...values)
    const last = values[values.length - 1]

    result.push({
      time: hourKey,
      value: formatValueWithUnit(avg, fmax.unit),
      unit: fmax.unit,
      rawValue: avg,
      avg: formatValueWithUnit(avg, fmax.unit),
      max: formatValueWithUnit(max, fmax.unit),
      min: formatValueWithUnit(min, fmax.unit),
      last: formatValueWithUnit(last, fmax.unit),
      rawAvg: avg,
      rawMax: max,
      rawMin: min,
      rawLast: last,
    })
  }

  // Sort by time
  result.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())

  return result
}

// Physical network interface name patterns
export const PHYSICAL_NIC_PATTERNS = [/^enp\d/, /^eth\d/, /^ens\d/, /^eno\d/, /^em\d/]

// Physical disk device patterns
export const PHYSICAL_DISK_PATTERNS = [/^vd[a-z]$/, /^sd[a-z]$/, /^nvme\d+n\d+$/]

function findPhysicalNicResult(results: any[]): any | undefined {
  if (!results || results.length === 0) return undefined

  // Try to find a physical NIC first
  for (const result of results) {
    const name = result.name || ''
    if (PHYSICAL_NIC_PATTERNS.some((pattern) => pattern.test(name))) {
      return result
    }
  }

  // Fallback to first result if no physical NIC found
  return results[0]
}

// Process a single network interface result into HostMonitorValue[]
function processNetworkInterfaceResult(
  result: any,
  filterDuration: number,
): { values: HostMonitorValue[]; isEmpty: boolean } {
  if (!result?.matrixArray || result.matrixArray.length === 0) {
    return { values: [], isEmpty: true }
  }

  const data = result.matrixArray.map((v: any) => ({
    time: dayjs(v?.matrix?.[0] * 1000),
    value: v?.matrix?.[1],
  }))

  const max = data.reduce((total: number, next: any) => {
    if (next.value > total) return next.value
    return total
  }, 0)

  const fmax = bytesToSize(max)
  const shouldAggregate = filterDuration > TIME_UNITS_IN_SECONDS.HOUR

  let values: HostMonitorValue[]
  if (shouldAggregate) {
    const aggregated = aggregateToHourly(data, fmax)
    const lastData = data.slice(-1)[0]
    if (lastData && lastData.value !== undefined) {
      const lastAggregated = aggregated[aggregated.length - 1]
      if (!lastAggregated || lastAggregated.time !== lastData.time.format()) {
        aggregated.push(convertToHostMonitorValue(lastData, fmax))
      }
    }
    values = aggregated
  } else {
    values = data.map((v: { time: dayjs.Dayjs; value?: number }) =>
      convertToHostMonitorValue(v, fmax),
    )
  }

  return { values, isEmpty: false }
}

// Build disk interface data from raw API response
export function buildDiskInterfaceData(
  rawData: any[],
  filterDuration: number,
): DiskInterfaceData[] {
  const readItem = rawData.find(
    (d) => enumToHostChartType(d.chartType) === HostChartType.DiskIOReadBytesPerSec,
  )
  const writeItem = rawData.find(
    (d) => enumToHostChartType(d.chartType) === HostChartType.DiskIOWriteBytesPerSec,
  )

  // Collect all device names
  const deviceNames = new Set<string>()
  readItem?.result?.forEach((r: any) => r.name && deviceNames.add(r.name))
  writeItem?.result?.forEach((r: any) => r.name && deviceNames.add(r.name))

  // Build data for each device
  return Array.from(deviceNames).map((name) => {
    const readResult = readItem?.result?.find((r: any) => r.name === name)
    const writeResult = writeItem?.result?.find((r: any) => r.name === name)

    const readProcessed = processNetworkInterfaceResult(readResult, filterDuration)
    const writeProcessed = processNetworkInterfaceResult(writeResult, filterDuration)

    return {
      name,
      readValues: readProcessed.values,
      writeValues: writeProcessed.values,
      isEmpty: readProcessed.isEmpty && writeProcessed.isEmpty,
    }
  })
}

// Process a single GPU result into HostMonitorValue[]
function processGpuResult(
  result: any,
  filterDuration: number,
): { values: HostMonitorValue[]; isEmpty: boolean } {
  if (!result?.matrixArray || result.matrixArray.length === 0) {
    return { values: [], isEmpty: true }
  }

  const data = result.matrixArray.map((v: any) => ({
    time: dayjs(v?.matrix?.[0] * 1000),
    value: v?.matrix?.[1],
  }))

  const fmax = { value: 100, unit: '%' } // GPU is always percentage
  const shouldAggregate = filterDuration > TIME_UNITS_IN_SECONDS.HOUR

  let values: HostMonitorValue[]
  if (shouldAggregate) {
    const aggregated = aggregateToHourly(data, fmax)
    const lastData = data.slice(-1)[0]
    if (lastData && lastData.value !== undefined) {
      const lastAggregated = aggregated[aggregated.length - 1]
      if (!lastAggregated || lastAggregated.time !== lastData.time.format()) {
        aggregated.push(convertToHostMonitorValue(lastData, fmax))
      }
    }
    values = aggregated
  } else {
    values = data.map((v: { time: dayjs.Dayjs; value?: number }) =>
      convertToHostMonitorValue(v, fmax),
    )
  }

  return { values, isEmpty: false }
}

// Build GPU device data from raw API response
export function buildGpuDeviceData(
  rawData: any[],
  chartType: HostChartType,
  filterDuration: number,
): GpuDeviceData[] {
  const gpuItem = rawData.find((d) => enumToHostChartType(d.chartType) === chartType)

  if (!gpuItem?.result) return []

  return gpuItem.result.map((r: any) => {
    const processed = processGpuResult(r, filterDuration)
    return {
      name: r.name || '0',
      values: processed.values,
      isEmpty: processed.isEmpty,
    }
  })
}

// Build network interface data from raw API response
export function buildNetworkInterfaceData(
  rawData: any[],
  filterDuration: number,
): NetworkInterfaceData[] {
  const txItem = rawData.find(
    (d) => enumToHostChartType(d.chartType) === HostChartType.NetworkTransmitBytesPerSec,
  )
  const rxItem = rawData.find(
    (d) => enumToHostChartType(d.chartType) === HostChartType.NetworkReceiveBytesPerSec,
  )

  // Collect all interface names
  const interfaceNames = new Set<string>()
  txItem?.result?.forEach((r: any) => r.name && interfaceNames.add(r.name))
  rxItem?.result?.forEach((r: any) => r.name && interfaceNames.add(r.name))

  // Build data for each interface
  return Array.from(interfaceNames).map((name) => {
    const txResult = txItem?.result?.find((r: any) => r.name === name)
    const rxResult = rxItem?.result?.find((r: any) => r.name === name)

    const txProcessed = processNetworkInterfaceResult(txResult, filterDuration)
    const rxProcessed = processNetworkInterfaceResult(rxResult, filterDuration)

    return {
      name,
      txValues: txProcessed.values,
      rxValues: rxProcessed.values,
      isEmpty: txProcessed.isEmpty && rxProcessed.isEmpty,
    }
  })
}

export const buildHostChartItemData = (
  item: any,
  timePoints: dayjs.Dayjs[],
  filterDuration: number,
): HostMonitorItemData => {
  const chartType = enumToHostChartType(item.chartType)
  let data: { time: dayjs.Dayjs; value?: number }[]
  let isEmpty = false

  // For network metrics, find physical NIC; otherwise use first result
  const isNetworkMetric =
    chartType === HostChartType.NetworkReceiveBytesPerSec ||
    chartType === HostChartType.NetworkTransmitBytesPerSec

  const selectedResult = isNetworkMetric ? findPhysicalNicResult(item.result) : item.result[0]

  if (!selectedResult?.matrixArray || selectedResult.matrixArray.length === 0) {
    data = timePoints.map((time) => ({ time }))
    isEmpty = true
  } else {
    data = selectedResult.matrixArray.map((v: any) => ({
      time: dayjs(v?.matrix?.[0] * 1000),
      value: v?.matrix?.[1],
    }))
  }

  const max = data.reduce((total: number, next: any) => {
    if (next.value > total) {
      return next.value
    }
    return total
  }, 0)

  const fmax = formatHostValue(chartType, max)
  const lastData = data.slice(-1)[0]
  const lastMonitorValue = lastData
    ? convertToHostMonitorValue(lastData, fmax)
    : { time: '', value: undefined, unit: fmax.unit }

  // Aggregate to hourly if duration > 1 hour
  const shouldAggregate = filterDuration > TIME_UNITS_IN_SECONDS.HOUR
  let values: HostMonitorValue[]

  if (shouldAggregate) {
    const aggregated = aggregateToHourly(data, fmax)

    // Append the latest raw data point if it's not at the hour boundary
    if (lastData && lastData.value !== undefined) {
      const lastAggregated = aggregated[aggregated.length - 1]

      // Only add if the latest point's time is different from the last aggregated hour
      if (!lastAggregated || lastAggregated.time !== lastData.time.format()) {
        aggregated.push(convertToHostMonitorValue(lastData, fmax))
      }
    }

    values = aggregated
  } else {
    values = data.map((v) => convertToHostMonitorValue(v, fmax))
  }

  return {
    type: chartType,
    titleKey: chartTitleKeyMap[chartType] || 'nodeMonitor.chartTitle.unknown',
    realtimeValue: formatRealtimeValue(chartType, lastMonitorValue),
    values,
    isEmpty,
  }
}

export function useNodeMonitorData({ nodeId, filterDuration }: UseNodeMonitorDataParams) {
  const [monitorDatas, setMonitorDatas] = useState<HostMonitorItemData[]>([])
  const [networkInterfaces, setNetworkInterfaces] = useState<NetworkInterfaceData[]>([])
  const [diskInterfaces, setDiskInterfaces] = useState<DiskInterfaceData[]>([])
  const [gpuUtilizationDevices, setGpuUtilizationDevices] = useState<GpuDeviceData[]>([])
  const [gpuRamUtilizationDevices, setGpuRamUtilizationDevices] = useState<GpuDeviceData[]>([])
  const [gpuCount, setGpuCount] = useState<number | null>(null)
  const [gpuDriverVersion, setGpuDriverVersion] = useState<string | null>(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const abortControllerRef = useRef<AbortController | null>(null)

  const fetchDetailMonitorData = useCallback(async () => {
    if (!nodeId) return

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    try {
      const res = await API.getDetailMonitorData({
        chart_type: [
          HostChartType.CpuUtilization,
          HostChartType.MemoryUtilization,
          HostChartType.DiskUtilization,
          HostChartType.DiskIOReadBytesPerSec,
          HostChartType.DiskIOWriteBytesPerSec,
          HostChartType.NetworkReceiveBytesPerSec,
          HostChartType.NetworkTransmitBytesPerSec,
          HostChartType.NvidiaGpuUtilization,
          HostChartType.NvidiaGpuRamUtilization,
        ],
        res_id: nodeId,
        time_interval_in_sec: filterDuration,
      })

      if (res.code === 0) {
        const timePoints = extractTimePoints(res.data || [])
        const processed = (res.data || []).map((it: any) => {
          return buildHostChartItemData(it, timePoints, filterDuration)
        })
        setMonitorDatas(processed)

        // Extract all network interfaces data
        const interfaces = buildNetworkInterfaceData(res.data || [], filterDuration)
        setNetworkInterfaces(interfaces)

        // Extract all disk interfaces data
        const disks = buildDiskInterfaceData(res.data || [], filterDuration)
        setDiskInterfaces(disks)

        // Extract GPU device data
        const gpuUtil = buildGpuDeviceData(
          res.data || [],
          HostChartType.NvidiaGpuUtilization,
          filterDuration,
        )
        setGpuUtilizationDevices(gpuUtil)

        const gpuRam = buildGpuDeviceData(
          res.data || [],
          HostChartType.NvidiaGpuRamUtilization,
          filterDuration,
        )
        setGpuRamUtilizationDevices(gpuRam)

        setInitialLoading(false)
      }
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name === 'AbortError') return
    }
  }, [nodeId, filterDuration])

  // Fetch GPU info (count and driver version) - only needs latest value
  const fetchGpuInfo = useCallback(async () => {
    if (!nodeId) return

    try {
      const res = await API.getDetailMonitorData({
        chart_type: [HostChartType.NvidiaGpuCount, HostChartType.NvidiaDriverVersion],
        res_id: nodeId,
        time_interval_in_sec: 1,
      })

      if (res.code === 0 && res.data) {
        // Extract GPU count
        const gpuCountItem = res.data.find(
          (d: any) => enumToHostChartType(d.chartType) === HostChartType.NvidiaGpuCount,
        )
        const gpuCountResult = gpuCountItem?.result?.[0] as any
        if (gpuCountResult?.matrixArray?.length > 0) {
          const lastValue = gpuCountResult.matrixArray[gpuCountResult.matrixArray.length - 1]
          setGpuCount(lastValue?.matrix?.[1] ?? null)
        }

        // Extract driver version (stored in result[0].name)
        const driverItem = res.data.find(
          (d: any) => enumToHostChartType(d.chartType) === HostChartType.NvidiaDriverVersion,
        )
        const driverResult = driverItem?.result?.[0] as any
        if (driverResult?.name) {
          setGpuDriverVersion(driverResult.name)
        }
      }
    } catch (error) {
      // Silently ignore GPU info fetch errors
    }
  }, [nodeId])

  // Interval for periodic refresh
  useInterval(fetchDetailMonitorData, 30000)

  // Immediate fetch on mount and when filterDuration changes
  useEffect(() => {
    fetchDetailMonitorData()
  }, [fetchDetailMonitorData])

  // Fetch GPU info once on mount
  useEffect(() => {
    fetchGpuInfo()
  }, [fetchGpuInfo])

  return {
    monitorDatas,
    networkInterfaces,
    diskInterfaces,
    gpuUtilizationDevices,
    gpuRamUtilizationDevices,
    gpuCount,
    gpuDriverVersion,
    initialLoading,
  }
}
